import {
	CheckIcon,
	CrossIcon,
	InfoIcon,
} from "@logora/parliament.icons.regular_icons";
import { ChamberLabel } from "@logora/parliament.parliament_chamber.chamber_label";
import { Label } from "@logora/parliament.ui.label";
import cx from "classnames";
import React, { useEffect, useRef, useCallback } from "react";
import styles from "./TimelineMilestone.module.scss";
import { TimelineStep } from "./TimelineStep";

export const TimelineMilestone = ({
	milestone,
	selectedActeUid,
	onChangeActe,
}) => {
	const milestoneRef = useRef(null);
	const { acteLegislatif, steps, title, status, decision, dateDecision } =
		milestone;
	const isSelected =
		acteLegislatif?.uid === selectedActeUid ||
		steps?.some((s) => s.acteLegislatif?.uid === selectedActeUid);
	const isCurrent =
		status === "active" || steps?.some((s) => s?.status === "active");

	useEffect(() => {
		if (isSelected && milestoneRef.current) {
			milestoneRef.current.scrollIntoView({
				behavior: "smooth",
				block: "nearest",
				inline: "center",
			});
		}
	}, [isSelected]);

	const formatDate = (date) => {
		if (!date) return null;
		return new Date(date).toLocaleDateString("fr", {
			year: "numeric",
			month: "long",
			day: "numeric",
		});
	};

	const date = formatDate(dateDecision || acteLegislatif?.dateActe);

	const onClickMilestone = useCallback(() => {
		if (onChangeActe && acteLegislatif) {
			if (acteLegislatif.dateActe && acteLegislatif.parentUid) {
				onChangeActe(acteLegislatif.uid);
			} else {
				onChangeActe(steps[0].acteLegislatif?.uid);
			}
		}
	}, [acteLegislatif, steps, onChangeActe]);

	const onClickStep = useCallback(
		(event, step) => {
			event.preventDefault();
			event.stopPropagation();

			if (onChangeActe && step.acteLegislatif) {
				onChangeActe(step.acteLegislatif?.uid);
			}
		},
		[onChangeActe],
	);

	const getDecisionStatus = useCallback(() => {
		if (decision) {
			if (
				decision.toLowerCase().startsWith("adopté") ||
				decision.toLowerCase().startsWith("accord")
			) {
				return "accepted";
			}
			if (decision.toLowerCase().startsWith("modifié")) {
				return "edited";
			}
			if (
				decision.toLowerCase().startsWith("rejeté") ||
				decision.toLowerCase().startsWith("accord")
			) {
				return "rejected";
			}
		}
	}, [decision]);

	const decisionStatus = getDecisionStatus();

	const getTitle = () => {
		if (acteLegislatif.codeActe === "CC") {
			return "Saisine du Conseil constitutionnel";
		}

		return title;
	};

	const getStepLabel = (step) => {
		if (acteLegislatif.codeActe === "CC") {
			return "Décision";
		}

		return step.title;
	};

	return (
		// biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
		<div
			ref={milestoneRef}
			className={cx(styles.milestone, styles[milestone.status], {
				[styles.milestoneSelected]: isSelected,
				[styles.milestoneCurrent]: isCurrent,
				[styles.withSteps]: steps.length > 0,
			})}
			onClick={onClickMilestone}
		>
			<div className={styles.header}>
				<h3 className={styles.title}>{getTitle()}</h3>

				{(acteLegislatif?.chambre || milestone.chambre) && (
					<ChamberLabel
						className={styles.subtitle}
						chamber={acteLegislatif?.chambre || milestone.chambre}
					/>
				)}
			</div>

			<div className={styles.nodeWrapper}>
				<div className={cx(styles.node, styles[milestone.status])}>
					<button
						onClick={onClickMilestone}
						type="button"
						className={styles.button}
					/>
				</div>
			</div>

			<div className={styles.footer}>
				<div className={styles.milestoneInformation}>
					{date && <div className={styles.date}>{date}</div>}
					{decision && decisionStatus && (
						<div>
							<Label
								className={styles[decisionStatus]}
								text={
									decisionStatus === "accepted"
										? "Texte adopté"
										: decisionStatus === "edited"
											? "Texte modifié"
											: "Texte rejeté"
								}
								rightIcon={
									decisionStatus === "accepted" ? (
										<CheckIcon height={20} width={20} />
									) : decisionStatus === "edited" ? (
										<InfoIcon height={20} width={20} />
									) : (
										<CrossIcon height={20} width={20} />
									)
								}
							/>
						</div>
					)}
				</div>
				<div className={styles.steps}>
					{steps.map((step, index) => (
						<TimelineStep
							key={`${step.acteLegislatif?.uid}-${index}`}
							selectedActeUid={selectedActeUid}
							onClickStep={(event) => onClickStep(event, step)}
							status={step.status}
							label={getStepLabel(step)}
							acteLegislatif={step.acteLegislatif}
						/>
					))}
				</div>
			</div>
		</div>
	);
};
